import React, { useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  AssignClassModal,
  AssignSubjectsModal,
  BackButton,
  ClassesSection,
  SubjectsSection,
  PhotoUploadModal,
} from "../../components";
import {
  FiChevronDown,
  FiCreditCard,
  FiEdit2,
  FiDownload,
  FiRefreshCw,
  FiAlertTriangle,
  FiCheckCircle,
  FiCamera,
} from "react-icons/fi";
import { BsQrCode } from "react-icons/bs";
import { useLocation } from "react-router-dom";
import { useStateContext } from "../../contexts/ContextProvider";
import { userRequest } from "../../utils/requestMethods";
import StaffBankDetailsModal from "../../components/Finance/StaffBankDetailsModal";
import { generateStaffIDCard } from "../../utils/idCardGenerator";

export default function ViewTeacher() {
  const location = useLocation();
  const history = useHistory();
  const teacher = location.state?.teacher;
  const assignClassId = location.state?.assignClassId || "";
  const { school } = useStateContext();
  const [board, setBoard] = useState("classes");
  const [assignClassModal, setAssignClassModal] = useState(Boolean(assignClassId));
  const [unassignClassModal, setUnassignClassModal] = useState(false);
  const [unassignSubjectModal, setUnassignSubjectModal] = useState(false);
  const [assignSubjectModal, setAssignSubjectModal] = useState(false);
  const [bulkUnassignModal, setBulkUnassignModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0); // Add a refresh key state
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [teacherBankDetails, setTeacherBankDetails] = useState(teacher?.counter_party || null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [staffPicture, setStaffPicture] = useState(teacher?.picture || null);

  // Staff ID Card States
  const [cardInfo, setCardInfo] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [cardActionLoading, setCardActionLoading] = useState(false);
  const [reissueModalOpen, setReissueModalOpen] = useState(false);
  const [downloadingCard, setDownloadingCard] = useState(false);

  const schoolId = school?._id;
  const teacherId = teacher?._id;

  // Fetch Staff ID Card Info
  const fetchStaffIdCard = useCallback(async () => {
    if (!schoolId || !teacherId) return;
    try {
      setCardLoading(true);
      setError("");
      // GET is a pure read — it returns qrCodeData directly when a card already
      // exists, so viewing this tab never needs to call the credential-issuing
      // "generate" endpoint (that stays reserved for the explicit
      // Generate/Reissue actions below).
      const { data } = await userRequest.get(`/clocking/${schoolId}/teachers/${teacherId}`);
      setCardInfo(data);
    } catch (err) {
      console.error("Error fetching staff ID card info:", err);
      setError(err.response?.data?.message || "Failed to load ID card info.");
    } finally {
      setCardLoading(false);
    }
  }, [schoolId, teacherId]);

  useEffect(() => {
    if (board === "idCard") {
      fetchStaffIdCard();
    }
  }, [board, fetchStaffIdCard]);

  const handleGenerateCard = async (reissue = false) => {
    if (!schoolId || !teacherId) return;
    try {
      setCardActionLoading(true);
      setError("");
      const { data } = await userRequest.post(`/clocking/${schoolId}/teachers/generate`, {
        teacherIds: [teacherId],
        reissue,
      });

      const result = data?.results?.[0];
      if (result?.success && result?.card) {
        setCardInfo({
          teacherId,
          hasCard: true,
          staffId: result.card.staffId,
          idCardVersion: result.card.idCardVersion,
          firstname: result.card.firstname,
          lastname: result.card.lastname,
          photo: result.card.photo,
          qrCodeData: result.card.qrCodeData,
          schoolName: result.card.schoolName,
        });
        setSuccess(
          reissue
            ? "Staff ID card reissued successfully!"
            : "Staff ID card generated successfully!",
        );
        setTimeout(() => setSuccess(""), 4000);
        setReissueModalOpen(false);
      } else {
        setError(result?.message || "Failed to generate ID card.");
        setTimeout(() => setError(""), 5000);
      }
    } catch (err) {
      console.error("Error generating staff ID card:", err);
      setError(err.response?.data?.message || "Failed to generate ID card.");
      setTimeout(() => setError(""), 5000);
    } finally {
      setCardActionLoading(false);
    }
  };

  const handleDownloadCard = async () => {
    if (!cardInfo?.qrCodeData) return;
    try {
      setDownloadingCard(true);
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(cardInfo.qrCodeData)}`;
      const staffPayload = {
        ...teacher,
        staff_id: cardInfo.staffId || teacher.staff_id,
        firstname: cardInfo.firstname || teacher.firstname,
        lastname: cardInfo.lastname || teacher.lastname,
        photo: cardInfo.photo || teacher.photo || teacher.picture?.path,
      };

      const dataUrl = await generateStaffIDCard(staffPayload, school, qrCodeUrl);
      if (dataUrl) {
        const link = document.createElement("a");
        link.download = `${teacher.firstname}_${teacher.lastname}_Staff_ID_Card.png`;
        link.href = dataUrl;
        link.click();
        setSuccess("ID card downloaded!");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      console.error("Error downloading ID card:", err);
      setError("Failed to download ID card image.");
      setTimeout(() => setError(""), 4000);
    } finally {
      setDownloadingCard(false);
    }
  };

  // Function to trigger re-fetching of data
  const refreshData = useCallback(() => {
    setRefreshKey((prevKey) => prevKey + 1);
  }, []);

  const theBody = document.body;
  const showAssignClassModal =
    "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-md";

  const handleShowAssignClassModal = () => {
    setAssignClassModal(true);
    theBody.style.overflow = "hidden";
  };

  const handleShowAssignSubjectModal = () => {
    setAssignSubjectModal(true);
    theBody.style.overflow = "hidden";
  };

  const handleShowUnassignClassModal = (classId) => {
    setSelectedClassId(classId);
    setUnassignClassModal(true);
    theBody.style.overflow = "hidden";
  };

  const handleHideUnassignClassModal = () => {
    setUnassignClassModal(false);
    theBody.style.overflow = "auto";
  };

  const handleShowUnassignSubjectModal = (classId, subjectId) => {
    setSelectedClassId(classId);
    setSelectedSubjectId(subjectId);
    setUnassignSubjectModal(true);
    theBody.style.overflow = "hidden";
  };

  const handleHideUnassignSubjectModal = () => {
    setUnassignSubjectModal(false);
    theBody.style.overflow = "auto";
  };

  const handleShowBulkUnassignModal = (subjects) => {
    setSelectedSubjects(subjects);
    setBulkUnassignModal(true);
    theBody.style.overflow = "hidden";
  };

  const handleHideBulkUnassignModal = () => {
    setBulkUnassignModal(false);
    setSelectedSubjects([]);
    theBody.style.overflow = "auto";
  };

  const handleUnassignClass = async () => {
    try {
      setLoading(true);
      setError("");

      if (!selectedClassId) {
        setError("No class selected for unassignment.");
        setLoading(false);
        return;
      }

      await userRequest.post(`/teachers/unassign-teacher-from-class`, {
        schoolId: school._id,
        teacherId: teacher._id,
        classId: selectedClassId,
      });

      setSuccess("Class unassigned successfully!");
      handleHideUnassignClassModal();
      refreshData(); // Refresh data instead of reloading page
    } catch (error) {
      console.error("Error unassigning class:", error);
      const payload = error.response?.data;
      setError(payload || { message: "An unexpected error occurred." });
      setTimeout(() => setError(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignSubject = async () => {
    try {
      setLoading(true);
      setError("");

      if (!selectedClassId) {
        setError("No class selected for unassignment.");
        setLoading(false);
        return;
      }

      await userRequest.post(`/teachers/unassign-teacher-from-subject`, {
        schoolId: school._id,
        teacherId: teacher._id,
        classId: selectedClassId,
        subjectId: selectedSubjectId,
      });

      setSuccess("Subject unassigned successfully!");
      handleHideUnassignSubjectModal();
      refreshData(); // Refresh data instead of reloading page
    } catch (error) {
      console.error("Error unassigning subject:", error);
      const payload = error.response?.data;
      setError(payload || { message: "An unexpected error occurred." });
      setTimeout(() => setError(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUnassignSubjects = async () => {
    try {
      setBulkLoading(true);
      setError("");

      if (!selectedSubjects || selectedSubjects.length === 0) {
        setError("No subjects selected for unassignment.");
        setBulkLoading(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      // Process each subject sequentially
      for (const subject of selectedSubjects) {
        try {
          await userRequest.post(`/teachers/unassign-teacher-from-subject`, {
            schoolId: school._id,
            teacherId: teacher._id,
            classId: subject.classId,
            subjectId: subject.subjectId,
          });
          successCount++;
        } catch (error) {
          console.error(`Error unassigning subject ${subject.subjectId}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        setSuccess(
          `${successCount} subject${successCount > 1 ? "s" : ""} unassigned successfully!`,
        );
        if (errorCount > 0) {
          setError(`${errorCount} subject${errorCount > 1 ? "s" : ""} failed to unassign.`);
        }
      } else {
        setError("Failed to unassign subjects.");
      }

      handleHideBulkUnassignModal();
      refreshData(); // Refresh data after bulk unassignment
    } catch (error) {
      console.error("Error in bulk unassignment:", error);
      setError("An unexpected error occurred during bulk unassignment.");
    } finally {
      setBulkLoading(false);
    }
  };

  const toErrorString = (payload) => {
    if (!payload) return "An unexpected error occurred.";
    if (typeof payload === "string") return payload;
    if (payload.message) return payload.message;
    if (payload.error) return payload.error;
    try {
      return JSON.stringify(payload);
    } catch {
      return "An unexpected error occurred.";
    }
  };

  const renderMessage = () => {
    if (success)
      return (
        <div className="fixed top-5 right-5 bg-green-500 text-white p-3 rounded-md shadow-md z-50">
          {success}
        </div>
      );
    if (error)
      return (
        <div className="fixed top-5 right-5 bg-red-500 text-white p-3 rounded-md shadow-md z-50">
          {toErrorString(error)}
        </div>
      );
    return null;
  };

  // location.state is lost on direct navigation, refresh, or a shared/bookmarked
  // link — every hook above already tolerates a missing teacher, so it's safe to
  // bail out here rather than crash on the unconditional teacher.X dereferences below.
  if (!teacher) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-24 md:py-9 px-8">
        <p className="text-gray-600 mb-4">
          We couldn&apos;t find this staff member&apos;s details. Please go back and select them
          again.
        </p>
        <button
          onClick={() => history.push("/management/teacher")}
          className="flex items-center justify-center py-2 mb-2 text-indigo-800"
        >
          <i className="fad fa-arrow-left mr-2"></i> Back to Staff List
        </button>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center justify-center py-24 md:py-9 px-8">
      {renderMessage()}
      {/* Modal box begins */}
      {assignClassModal && (
        <AssignClassModal
          onClose={() => {
            setAssignClassModal(false);
            theBody.style.overflow = "auto";
          }}
          teacher={teacher}
          school={school}
          initialClassId={assignClassId}
          lockClass={Boolean(assignClassId)}
          onSuccess={() => {
            setSuccess("Class assigned successfully!");
            setTimeout(() => setSuccess(false), 3000);
            refreshData(); // Refresh data on successful assignment
          }}
        />
      )}

      {assignSubjectModal && (
        <AssignSubjectsModal
          onClose={() => {
            setAssignSubjectModal(false);
            theBody.style.overflow = "auto";
          }}
          teacher={teacher}
          school={school}
          onSuccess={() => {
            setSuccess("Subject assigned successfully!");
            setTimeout(() => setSuccess(false), 3000);
            refreshData(); // Refresh data on successful assignment
          }}
        />
      )}

      {/* Unassign subject modal */}
      {unassignSubjectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-md">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="p-3 bg-[#EEEEFC] w-max rounded mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M18.3327 9.2333V9.99997C18.3317 11.797 17.7498 13.5455 16.6738 14.9848C15.5978 16.4241 14.0854 17.477 12.3621 17.9866C10.6389 18.4961 8.79707 18.4349 7.11141 17.8121C5.42575 17.1894 3.98656 16.0384 3.00848 14.5309C2.0304 13.0233 1.56584 11.24 1.68408 9.4469C1.80232 7.65377 2.49702 5.94691 3.66458 4.58086C4.83214 3.21482 6.41 2.26279 8.16284 1.86676C9.91568 1.47073 11.7496 1.65192 13.391 2.3833M18.3327 3.3333L9.99935 11.675L7.49935 9.17497"
                  stroke="#8281CD"
                  stroke-width="1.66667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p className="font-semibold mb-2">Unassign Subject</p>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to unassign this subject?
            </p>

            <div className="flex justify-end space-x-2">
              <button
                onClick={handleHideUnassignSubjectModal}
                className="text-sm text-blue-500 bg-white border border-gray-300 hover:bg-blue-100 px-4 py-2 rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleUnassignSubject}
                className="text-sm bg-red-600 border border-transparent hover:bg-red-800 text-white px-4 py-2 rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? "Unassigning..." : "Unassign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unassign class modal */}
      {unassignClassModal && (
        <div className={showAssignClassModal}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="p-3 bg-[#EEEEFC] w-max rounded mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M18.3327 9.2333V9.99997C18.3317 11.797 17.7498 13.5455 16.6738 14.9848C15.5978 16.4241 14.0854 17.477 12.3621 17.9866C10.6389 18.4961 8.79707 18.4349 7.11141 17.8121C5.42575 17.1894 3.98656 16.0384 3.00848 14.5309C2.0304 13.0233 1.56584 11.24 1.68408 9.4469C1.80232 7.65377 2.49702 5.94691 3.66458 4.58086C4.83214 3.21482 6.41 2.26279 8.16284 1.86676C9.91568 1.47073 11.7496 1.65192 13.391 2.3833M18.3327 3.3333L9.99935 11.675L7.49935 9.17497"
                  stroke="#8281CD"
                  stroke-width="1.66667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p className="font-semibold mb-2">Unassign Class</p>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to unassign this class?
            </p>

            <div className="flex justify-end space-x-2">
              <button
                onClick={handleHideUnassignClassModal}
                className="text-sm text-blue-500 bg-white border border-gray-300 hover:bg-blue-100 px-4 py-2 rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleUnassignClass}
                className="text-sm bg-red-600 border border-transparent hover:bg-red-800 text-white px-4 py-2 rounded-full disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? "Unassigning..." : "Unassign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk unassign subjects modal */}
      {bulkUnassignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-md">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="p-3 bg-[#EEEEFC] w-max rounded mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M18.3327 9.2333V9.99997C18.3317 11.797 17.7498 13.5455 16.6738 14.9848C15.5978 16.4241 14.0854 17.477 12.3621 17.9866C10.6389 18.4961 8.79707 18.4349 7.11141 17.8121C5.42575 17.1894 3.98656 16.0384 3.00848 14.5309C2.0304 13.0233 1.56584 11.24 1.68408 9.4469C1.80232 7.65377 2.49702 5.94691 3.66458 4.58086C4.83214 3.21482 6.41 2.26279 8.16284 1.86676C9.91568 1.47073 11.7496 1.65192 13.391 2.3833M18.3327 3.3333L9.99935 11.675L7.49935 9.17497"
                  stroke="#8281CD"
                  stroke-width="1.66667"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p className="font-semibold mb-2">Unassign Multiple Subjects</p>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to unassign {selectedSubjects.length} selected subject
              {selectedSubjects.length > 1 ? "s" : ""}? This action will be performed one by one and
              cannot be undone.
            </p>

            <div className="flex justify-end space-x-2">
              <button
                onClick={handleHideBulkUnassignModal}
                className="text-sm text-blue-500 bg-white border border-gray-300 hover:bg-blue-100 px-4 py-2 rounded-full"
                disabled={bulkLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUnassignSubjects}
                className="text-sm bg-red-600 border border-transparent hover:bg-red-800 text-white px-4 py-2 rounded-full disabled:opacity-50"
                disabled={bulkLoading}
              >
                {bulkLoading ? "Unassigning..." : "Unassign All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal boxes end */}
      <div className="bg-white w-full px-7 py-9 rounded-[40px]">
        <BackButton history={history} />

        <div className="flex flex-wrap items-start justify-between mt-4">
          <div className="sm:w-max w-full sm:mb-0 mb-3">
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                {staffPicture?.path ? (
                  <img
                    src={staffPicture.path}
                    alt={`${teacher.firstname} ${teacher.lastname}`}
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#C5C5E8] text-[#3F3DA5] flex items-center justify-center text-xl font-semibold border-2 border-gray-200">
                    {teacher.firstname?.[0]}
                    {teacher.lastname?.[0]}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setPhotoModalOpen(true)}
                  className="absolute -bottom-1 -right-1 bg-[#3F3DA5] hover:bg-blue-800 text-white rounded-full p-1.5 shadow"
                  title="Update photo"
                >
                  <FiCamera className="w-3.5 h-3.5" />
                </button>
              </div>

              <div>
                <div className="flex items-center">
                  {" "}
                  <p className="inline text-2xl font-semibold">
                    {teacher.firstname} {teacher.lastname}
                  </p>{" "}
                  <button className="flex items-center justify-between bg-[#5FC3811A] text-[#5FC381] text-[14px] py-1 px-2 rounded-full ml-3">
                    <p className="mr-1">Active</p> <FiChevronDown />{" "}
                  </button>
                </div>
                <p className="text-[14px] mt-3">{teacher.email}</p>
                <p className="text-[14px] mt-3">{teacher.mobile_number}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end sm:w-max w-full">
            <button
              onClick={handleShowAssignClassModal}
              className="sm:w-max w-full bg-[#C5C5E8] hover:bg-indigo-300 text-[#3F3DA5] sm:mr-4 mr-0 font-medium text-sm px-4 py-2 rounded-full items-center justify-between"
            >
              Assign Class
              <svg
                className="inline ml-2"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M8 0.75C8.69 0.75 9.25 1.31 9.25 2V6.75H14C14.3315 6.75 14.6495 6.8817 14.8839 7.11612C15.1183 7.35054 15.25 7.66848 15.25 8C15.25 8.33152 15.1183 8.64946 14.8839 8.88388C14.6495 9.1183 14.3315 9.25 14 9.25H9.25V14C9.25 14.3315 9.1183 14.6495 8.88388 14.8839C8.64946 15.1183 8.33152 15.25 8 15.25C7.66848 15.25 7.35054 15.1183 7.11612 14.8839C6.8817 14.6495 6.75 14.3315 6.75 14V9.25H2C1.66848 9.25 1.35054 9.1183 1.11612 8.88388C0.881696 8.64946 0.75 8.33152 0.75 8C0.75 7.66848 0.881696 7.35054 1.11612 7.11612C1.35054 6.8817 1.66848 6.75 2 6.75H6.75V2C6.75 1.31 7.31 0.75 8 0.75Z"
                  fill="#3F3DA5"
                />
              </svg>
            </button>

            <button
              onClick={handleShowAssignSubjectModal}
              className="sm:w-max w-full sm:mt-0 mt-3 bg-[#4543B5] hover:bg-indigo-900 text-white font-medium text-sm px-4 py-2 rounded-full items-center justify-between"
            >
              Assign Subject
              <svg
                className="inline ml-2"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
              >
                <path
                  d="M8 0.330078C8.69 0.330078 9.25 0.890078 9.25 1.58008V6.33008H14C14.3315 6.33008 14.6495 6.46177 14.8839 6.69619C15.1183 6.93061 15.25 7.24856 15.25 7.58008C15.25 7.9116 15.1183 8.22954 14.8839 8.46396C14.6495 8.69838 14.3315 8.83008 14 8.83008H9.25V13.5801C9.25 13.9116 9.1183 14.2295 8.88388 14.464C8.64946 14.6984 8.33152 14.8301 8 14.8301C7.66848 14.8301 7.35054 14.6984 7.11612 14.464C6.8817 14.2295 6.75 13.9116 6.75 13.5801V8.83008H2C1.66848 8.83008 1.35054 8.69838 1.11612 8.46396C0.881696 8.22954 0.75 7.9116 0.75 7.58008C0.75 7.24856 0.881696 6.93061 1.11612 6.69619C1.35054 6.46177 1.66848 6.33008 2 6.33008H6.75V1.58008C6.75 0.890078 7.31 0.330078 8 0.330078Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Bank Details Card */}
      <div className="bg-white w-full px-7 py-6 rounded-[40px] mt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FiCreditCard className="text-indigo-600" />
            <h3 className="text-lg font-semibold">Bank Account Details</h3>
          </div>
          <button
            onClick={() => setBankModalOpen(true)}
            className="flex items-center gap-1.5 bg-[#C5C5E8] hover:bg-indigo-300 text-[#3F3DA5] font-medium text-sm px-4 py-2 rounded-full transition-colors"
          >
            <FiEdit2 className="w-3.5 h-3.5" />
            {teacherBankDetails?.account_number ? "Edit" : "Set Bank Details"}
          </button>
        </div>
        {teacherBankDetails?.account_number ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Bank Name</p>
              <p className="text-sm font-medium text-gray-800">
                {teacherBankDetails.bank_name || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Account Number</p>
              <p className="text-sm font-medium text-gray-800">
                {teacherBankDetails.account_number}
              </p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Account Name</p>
              <p className="text-sm font-medium text-gray-800">
                {teacherBankDetails.account_name || "—"}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl px-4 py-3 text-sm">
            <FiCreditCard className="shrink-0" />
            <span>
              No bank account linked yet. Set bank details to enable salary disbursements for this
              staff member.
            </span>
          </div>
        )}
      </div>

      {/* Bank Details Modal */}
      <StaffBankDetailsModal
        isOpen={bankModalOpen}
        onClose={() => setBankModalOpen(false)}
        schoolId={school?._id}
        staffMember={teacher ? { ...teacher, counter_party: teacherBankDetails } : null}
        onSaved={(staffId, updatedCp) => {
          setTeacherBankDetails(updatedCp);
          setBankModalOpen(false);
        }}
      />

      {/* Staff Photo Upload Modal */}
      <PhotoUploadModal
        isOpen={photoModalOpen}
        onClose={() => setPhotoModalOpen(false)}
        uploadEndpoint={`/users/${schoolId}/upload-photo/${teacherId}`}
        title="Update Staff Photo"
        onUploadSuccess={(picture) => {
          setStaffPicture(picture);
          setSuccess("Photo updated successfully.");
        }}
      />

      {/* Reissue Confirmation Modal */}
      {reissueModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="w-12 h-12 bg-amber-100 text-amber-700 rounded-xl flex items-center justify-center mb-4">
              <FiAlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">Reissue Staff ID Card?</h3>
            <p className="text-sm text-slate-600 mb-6">
              Reissuing this card will bump the ID card version number and{" "}
              <strong>immediately invalidate</strong> any existing physical or printed cards. The
              staff member will need the new card to clock in.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setReissueModalOpen(false)}
                disabled={cardActionLoading}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleGenerateCard(true)}
                disabled={cardActionLoading}
                className="px-5 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-full transition-colors flex items-center gap-2"
              >
                {cardActionLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                    <span>Reissuing...</span>
                  </>
                ) : (
                  <>
                    <FiRefreshCw className="w-3.5 h-3.5" />
                    <span>Confirm Reissue</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white w-full rounded-[40px] p-6 mt-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setBoard("classes")}
            className={
              board === "classes"
                ? "sm:w-max w-full bg-[#C5C5E8] border border-[#C5C5E8] text-[#3F3DA5] font-semibold text-sm px-5 py-2 rounded-full transition-colors"
                : "sm:w-max w-full bg-white hover:bg-indigo-50 border border-slate-200 text-slate-600 font-medium text-sm px-5 py-2 rounded-full transition-colors"
            }
          >
            Assigned Classes
          </button>

          <button
            onClick={() => setBoard("subjects")}
            className={
              board === "subjects"
                ? "sm:w-max w-full bg-[#C5C5E8] border border-[#C5C5E8] text-[#3F3DA5] font-semibold text-sm px-5 py-2 rounded-full transition-colors"
                : "sm:w-max w-full bg-white hover:bg-indigo-50 border border-slate-200 text-slate-600 font-medium text-sm px-5 py-2 rounded-full transition-colors"
            }
          >
            Assigned Subjects
          </button>

          <button
            onClick={() => setBoard("idCard")}
            className={
              board === "idCard"
                ? "sm:w-max w-full bg-[#C5C5E8] border border-[#C5C5E8] text-[#3F3DA5] font-semibold text-sm px-5 py-2 rounded-full transition-colors"
                : "sm:w-max w-full bg-white hover:bg-indigo-50 border border-slate-200 text-slate-600 font-medium text-sm px-5 py-2 rounded-full transition-colors"
            }
          >
            Staff ID Card
          </button>
        </div>

        <div className="mt-6">
          {board === "classes" && (
            <ClassesSection
              teacher={teacher}
              school={school}
              handleShowUnassignClassModal={handleShowUnassignClassModal}
              refreshKey={refreshKey}
            />
          )}

          {board === "subjects" && (
            <SubjectsSection
              teacher={teacher}
              school={school}
              handleShowUnassignSubjectModal={handleShowUnassignSubjectModal}
              handleShowBulkUnassignModal={handleShowBulkUnassignModal}
              refreshKey={refreshKey}
            />
          )}

          {board === "idCard" && (
            <div className="p-2">
              {cardLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-200 border-t-indigo-600 mb-3"></div>
                  <p className="text-slate-500 text-sm">Checking ID card status...</p>
                </div>
              ) : cardInfo?.hasCard ? (
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 sm:p-8">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-200">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold text-slate-800">Staff ID Card</h3>
                        <span className="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <FiCheckCircle className="w-3 h-3" /> Active
                        </span>
                        {cardInfo.idCardVersion && (
                          <span className="bg-indigo-100 text-indigo-700 text-xs font-mono font-medium px-2 py-0.5 rounded-full">
                            v{cardInfo.idCardVersion}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-500 text-xs md:text-sm mt-1">
                        Staff ID:{" "}
                        <strong className="text-slate-700 font-mono">
                          {cardInfo.staffId || "—"}
                        </strong>
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setReissueModalOpen(true)}
                        disabled={cardActionLoading}
                        className="px-4 py-2 text-xs sm:text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-full transition-colors flex items-center gap-1.5"
                      >
                        <FiRefreshCw className="w-3.5 h-3.5" />
                        <span>Reissue Card</span>
                      </button>

                      {cardInfo.qrCodeData && (
                        <button
                          type="button"
                          onClick={handleDownloadCard}
                          disabled={downloadingCard}
                          className="px-5 py-2 text-xs sm:text-sm font-medium text-white bg-[#3F3DA5] hover:bg-indigo-900 rounded-full shadow-md transition-colors flex items-center gap-1.5"
                        >
                          {downloadingCard ? (
                            <>
                              <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent"></div>
                              <span>Preparing...</span>
                            </>
                          ) : (
                            <>
                              <FiDownload className="w-3.5 h-3.5" />
                              <span>Download ID Card</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* QR Code and Card Preview */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 items-center">
                    <div className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
                      {cardInfo.qrCodeData ? (
                        <>
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(cardInfo.qrCodeData)}`}
                            alt="Staff Clocking QR Code"
                            className="w-48 h-48 sm:w-56 sm:h-56 rounded-xl border border-slate-100 p-2 shadow-inner"
                          />
                          <p className="text-xs font-semibold text-slate-700 mt-3">
                            Clock-in QR Code
                          </p>
                          <p className="text-[11px] text-slate-400">
                            Scan at school clocking station
                          </p>
                        </>
                      ) : (
                        <div className="py-8 text-center text-slate-400">
                          <BsQrCode className="w-16 h-16 mx-auto mb-2 opacity-40" />
                          <p className="text-xs">QR code payload not cached</p>
                          <button
                            onClick={() => handleGenerateCard(false)}
                            disabled={cardActionLoading}
                            className="mt-3 text-xs text-indigo-600 font-semibold underline"
                          >
                            Refresh QR Code
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-4 text-sm text-slate-700">
                      <div className="bg-white p-5 rounded-2xl border border-slate-200">
                        <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-3">
                          Card Information
                        </h4>
                        <div className="space-y-2.5 text-xs">
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-400">Full Name</span>
                            <span className="font-medium text-slate-800">
                              {teacher.firstname} {teacher.lastname}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-400">Staff ID</span>
                            <span className="font-mono font-medium text-slate-800">
                              {cardInfo.staffId || "—"}
                            </span>
                          </div>
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-slate-400">Card Version</span>
                            <span className="font-mono font-medium text-indigo-700">
                              v{cardInfo.idCardVersion || 1}
                            </span>
                          </div>
                          <div className="flex justify-between py-1">
                            <span className="text-slate-400">Designation</span>
                            <span className="font-medium text-slate-800">
                              {teacher.role === 1 || teacher.role === "teacher"
                                ? "Teacher"
                                : "Staff"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-xs text-indigo-900 flex items-start gap-2.5">
                        <FiAlertTriangle className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                        <span>
                          The physical/digital card can be scanned by the authorised school clocking
                          station or by this staff member on their registered device.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-10 text-center">
                  <div className="w-16 h-16 bg-indigo-100 text-indigo-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <BsQrCode className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">
                    No Staff ID Card Issued Yet
                  </h3>
                  <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
                    Generate a unique signed QR Staff ID Card for {teacher.firstname}{" "}
                    {teacher.lastname} to enable attendance clock-ins at the school station.
                  </p>
                  <button
                    type="button"
                    onClick={() => handleGenerateCard(false)}
                    disabled={cardActionLoading}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#3F3DA5] hover:bg-indigo-900 rounded-full shadow-lg transition-all"
                  >
                    {cardActionLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        <span>Generating ID Card...</span>
                      </>
                    ) : (
                      <>
                        <BsQrCode className="w-4 h-4" />
                        <span>Generate Staff ID Card</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
